/**
 * Utilitário centralizado para remoção de emojis em campos de definição
 * de templates do WhatsApp Oficial.
 *
 * Motivação:
 * - O Meta passou a rejeitar a aprovação de templates que contêm emojis
 *   nos textos de definição (header/body/footer e textos de botões).
 * - Aplicamos a remoção tanto na digitação (UX imediata) quanto como
 *   defesa em profundidade no submit e na edge function `manage-template`.
 *
 * Cobertura do regex:
 * - \p{Extended_Pictographic} (todos pictográficos: emoji clássicos, símbolos)
 * - \uFE0F (Variation Selector-16 — força apresentação como emoji)
 * - \u200D (Zero-Width Joiner — usado em emojis compostos como família)
 * - \p{Emoji_Modifier} (modificadores de tom de pele)
 * - \p{Regional_Indicator} (bandeiras — pares de regionais)
 *
 * Importante: NÃO remove caracteres acentuados, pontuação, símbolos
 * matemáticos comuns nem variáveis {{1}} {{2}} usadas no template.
 */

import { toast } from 'sonner';
import { useRef, useCallback } from 'react';

// Regex consolidado que captura emojis em todas as suas formas
// Flag `gu` necessária para Unicode property escapes
export const EMOJI_REGEX =
  /[\p{Extended_Pictographic}\p{Emoji_Modifier}\p{Regional_Indicator}\uFE0F\u200D]/gu;

/**
 * Remove todos os emojis de um texto.
 * @returns objeto com o texto limpo e flag indicando se algum emoji foi removido
 */
export function stripEmojis(text: string): { clean: string; removed: boolean } {
  if (!text) return { clean: text ?? '', removed: false };
  const clean = text.replace(EMOJI_REGEX, '');
  return { clean, removed: clean !== text };
}

/**
 * Verifica se um texto contém emoji (sem alterá-lo).
 * Usado em validações de submit e no backend.
 */
export function containsEmoji(text: string | null | undefined): boolean {
  if (!text) return false;
  EMOJI_REGEX.lastIndex = 0; // regex global precisa ser resetado
  return EMOJI_REGEX.test(text);
}

/**
 * Hook que devolve um onChange "blindado" contra emojis.
 *
 * Comportamento:
 * - Aplica `stripEmojis` no valor recebido
 * - Chama o setter com o valor limpo
 * - Exibe toast de aviso (com debounce de 1.5s para não floodar
 *   quando o usuário cola um texto longo com vários emojis)
 *
 * Uso:
 *   const onBodyChange = useEmojiSafeHandler(setBody);
 *   <Textarea onChange={(e) => onBodyChange(e.target.value)} />
 */
export function useEmojiSafeHandler(
  setter: (value: string) => void
): (value: string) => void {
  // Timestamp do último toast exibido — evita spam
  const lastToastAtRef = useRef<number>(0);

  return useCallback(
    (value: string) => {
      const { clean, removed } = stripEmojis(value);
      setter(clean);

      if (removed) {
        const now = Date.now();
        // Debounce de 1.5s entre toasts
        if (now - lastToastAtRef.current > 1500) {
          lastToastAtRef.current = now;
          toast.warning(
            'Emojis não são permitidos em templates do WhatsApp Oficial. O caractere foi removido.'
          );
        }
      }
    },
    [setter]
  );
}
